import { DateTime } from 'luxon';

import { eventShowsAsBusy, type PlanEvent } from '@/features/calendars/PlanEventSheet';
import { blockerTitle, type Blocker, type TimeBlock } from '@/features/calendars/types';
import { blockTitle } from '@/features/calendars/scheduleConflict';
import type { Task } from '@/features/tasks/types';

export type AgendaItemKind = 'block' | 'event' | 'task' | 'blocker';

export interface AgendaItem {
  key: string;
  kind: AgendaItemKind;
  title: string;
  /** Milliseconds since epoch for sorting; lower = earlier. */
  sortAt: number;
  timeLabel: string;
  color: string;
  done: boolean;
  past: boolean;
  active: boolean;
  block?: TimeBlock;
  event?: PlanEvent;
  task?: Task;
  blocker?: Blocker;
}

function formatTimeLabel(
  startsAt: DateTime,
  endsAt: DateTime | null,
  allDay: boolean,
): string {
  if (allDay) return 'All day';
  const start = startsAt.toFormat('h:mm a');
  if (!endsAt) return start;
  return `${start} – ${endsAt.toFormat('h:mm a')}`;
}

function isDueToday(task: Task, dayStart: DateTime, dayEnd: DateTime): boolean {
  if (!task.due_at) return false;
  const due = DateTime.fromISO(task.due_at, { zone: 'utc' }).setZone(dayStart.zone);
  return due >= dayStart && due <= dayEnd;
}

function isCompletedOnDay(task: Task, dayStart: DateTime, dayEnd: DateTime): boolean {
  if (task.status !== 'done' || !task.completed_at) return false;
  const doneAt = DateTime.fromISO(task.completed_at, { zone: 'utc' }).setZone(dayStart.zone);
  return doneAt >= dayStart && doneAt <= dayEnd;
}

/**
 * Merge scheduled blocks, calendar events, and unscheduled due/overdue tasks
 * into one chronological list for the Daily page.
 *
 * Completed work that never had a planner slot still belongs in the day's
 * completed section: finishing something off-plan is still finishing it today.
 */
export function buildTodayAgenda({
  blocks,
  events,
  blockers = [],
  overdueTasks,
  dueTodayTasks,
  completedTodayTasks = [],
  timezone,
  now = DateTime.now(),
  activeTaskId,
}: {
  blocks: TimeBlock[];
  events: PlanEvent[];
  /** Shown for context, never counted as work. */
  blockers?: Blocker[];
  overdueTasks: Task[];
  dueTodayTasks: Task[];
  /** Done today, with or without a planner block. */
  completedTodayTasks?: Task[];
  timezone: string;
  now?: DateTime;
  activeTaskId?: number | null;
}): AgendaItem[] {
  const zoneNow = now.setZone(timezone);
  const dayStart = zoneNow.startOf('day');
  const dayEnd = zoneNow.endOf('day');

  const blockTaskIds = new Set(blocks.map((b) => b.task_id));
  const items: AgendaItem[] = [];

  for (const block of blocks) {
    if (block.status === 'skipped') continue;

    const start = DateTime.fromISO(block.starts_at, { zone: 'utc' }).setZone(timezone);
    const end = DateTime.fromISO(block.ends_at, { zone: 'utc' }).setZone(timezone);
    const done = block.status === 'completed';
    const past = end < zoneNow;

    items.push({
      key: `block-${block.id}`,
      kind: 'block',
      title: blockTitle(block),
      sortAt: start.toMillis(),
      timeLabel: formatTimeLabel(start, end, false),
      color: block.client_color,
      done,
      past,
      active: activeTaskId === block.task_id,
      block,
    });
  }

  for (const event of events) {
    // Free / non-busy imports are day to-dos elsewhere, not agenda work.
    if (!eventShowsAsBusy(event)) continue;

    const start = DateTime.fromISO(event.starts_at, { zone: 'utc' }).setZone(timezone);
    const end = DateTime.fromISO(event.ends_at, { zone: 'utc' }).setZone(timezone);
    const done = Boolean(event.completed_at);
    const past = (event.all_day ? dayEnd : end) < zoneNow;

    items.push({
      key: `event-${event.id}`,
      kind: 'event',
      title: event.title || '(No title)',
      sortAt: event.all_day ? dayStart.toMillis() : start.toMillis(),
      timeLabel: formatTimeLabel(start, event.all_day ? null : end, event.all_day),
      color: event.color ?? 'var(--accent)',
      done,
      past,
      active: false,
      event,
    });
  }

  for (const blocker of blockers) {
    const start = DateTime.fromISO(blocker.starts_at, { zone: 'utc' }).setZone(timezone);
    const end = DateTime.fromISO(blocker.ends_at, { zone: 'utc' }).setZone(timezone);

    items.push({
      key: `blocker-${blocker.id}`,
      kind: 'blocker',
      title: blockerTitle(blocker),
      sortAt: start.toMillis(),
      timeLabel: formatTimeLabel(start, end, false),
      color: 'var(--border-strong)',
      // A blocker is never done and never undone -- it just passes.
      done: false,
      past: end < zoneNow,
      active: false,
      blocker,
    });
  }

  const unscheduledOverdue = overdueTasks.filter(
    (t) => !blockTaskIds.has(t.id) && t.status !== 'done' && t.status !== 'dropped',
  );
  for (const task of unscheduledOverdue) {
    items.push({
      key: `task-overdue-${task.id}`,
      kind: 'task',
      title: task.title,
      sortAt: dayStart.toMillis() - 2,
      timeLabel: 'Overdue',
      color: 'var(--warning)',
      done: false,
      past: true,
      active: activeTaskId === task.id,
      task,
    });
  }

  const unscheduledDue = dueTodayTasks.filter(
    (t) =>
      !blockTaskIds.has(t.id) &&
      t.status !== 'done' &&
      t.status !== 'dropped' &&
      !unscheduledOverdue.some((o) => o.id === t.id),
  );
  for (const task of unscheduledDue) {
    items.push({
      key: `task-due-${task.id}`,
      kind: 'task',
      title: task.title,
      sortAt: dayStart.toMillis() - 1,
      timeLabel: 'Due today',
      color: 'var(--accent-border)',
      done: false,
      past: false,
      active: activeTaskId === task.id,
      task,
    });
  }

  // Off-plan completions: finished today, never (or no longer) on the grid.
  for (const task of completedTodayTasks) {
    if (blockTaskIds.has(task.id)) continue;
    if (task.status !== 'done' || !task.completed_at) continue;

    const doneAt = DateTime.fromISO(task.completed_at, { zone: 'utc' }).setZone(timezone);
    items.push({
      key: `task-done-${task.id}`,
      kind: 'task',
      title: task.title,
      sortAt: doneAt.toMillis(),
      timeLabel: doneAt.toFormat('h:mm a'),
      color: 'var(--accent-border)',
      done: true,
      past: true,
      active: false,
      task,
    });
  }

  items.sort((a, b) => {
    if (a.sortAt !== b.sortAt) return a.sortAt - b.sortAt;
    if (a.kind === 'event' && b.kind !== 'event') return -1;
    if (b.kind === 'event' && a.kind !== 'event') return 1;
    return a.title.localeCompare(b.title);
  });

  return items;
}

export function isDueTodayTask(task: Task, timezone: string): boolean {
  const zoneNow = DateTime.now().setZone(timezone);
  return isDueToday(task, zoneNow.startOf('day'), zoneNow.endOf('day'));
}

export function isCompletedTodayTask(task: Task, timezone: string, now = DateTime.now()): boolean {
  const zoneNow = now.setZone(timezone);
  return isCompletedOnDay(task, zoneNow.startOf('day'), zoneNow.endOf('day'));
}

export function countAgendaStats(blocks: TimeBlock[], events: PlanEvent[]) {
  const plannedTasks = blocks.filter((b) => b.status !== 'skipped').length;
  // Free / non-busy appointments are day to-dos, not calendar load.
  const appointments = events.filter((e) => eventShowsAsBusy(e)).length;
  return { plannedTasks, appointments };
}

type IntervalMs = { start: number; end: number };

/** Merge overlapping [start, end) intervals; sum unique covered minutes. */
export function mergedMinutes(intervals: IntervalMs[]): number {
  const sorted = intervals
    .filter((i) => i.end > i.start)
    .sort((a, b) => a.start - b.start || a.end - b.end);
  if (sorted.length === 0) return 0;

  let covered = 0;
  let curStart = sorted[0]!.start;
  let curEnd = sorted[0]!.end;

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i]!;
    if (next.start <= curEnd) {
      curEnd = Math.max(curEnd, next.end);
    } else {
      covered += curEnd - curStart;
      curStart = next.start;
      curEnd = next.end;
    }
  }
  covered += curEnd - curStart;
  return Math.round(covered / 60_000);
}

/**
 * Total scheduled minutes today: unique clock time covered by time blocks and
 * busy appointments (overlaps count once), plus estimates for unscheduled
 * due/overdue tasks that are not on the calendar.
 */
export function computeScheduledMinutes(
  blocks: TimeBlock[],
  events: PlanEvent[],
  {
    timezone,
    dayStartMinute = 420,
    dayEndMinute = 1200,
    dueTodayTasks = [],
    overdueTasks = [],
  }: {
    timezone: string;
    dayStartMinute?: number;
    dayEndMinute?: number;
    dueTodayTasks?: Task[];
    overdueTasks?: Task[];
  },
): number {
  const blockTaskIds = new Set<number>();
  const intervals: IntervalMs[] = [];

  for (const block of blocks) {
    if (block.status === 'skipped') continue;
    blockTaskIds.add(block.task_id);
    intervals.push({
      start: new Date(block.starts_at).getTime(),
      end: new Date(block.ends_at).getTime(),
    });
  }

  const dayStart = DateTime.now().setZone(timezone).startOf('day');
  const workDayStart = dayStart.plus({ minutes: dayStartMinute }).toMillis();
  const workDayEnd = dayStart.plus({ minutes: dayEndMinute }).toMillis();

  for (const event of events) {
    if (!eventShowsAsBusy(event)) continue;
    if (event.all_day) {
      intervals.push({ start: workDayStart, end: workDayEnd });
      continue;
    }
    intervals.push({
      start: DateTime.fromISO(event.starts_at, { zone: 'utc' }).toMillis(),
      end: DateTime.fromISO(event.ends_at, { zone: 'utc' }).toMillis(),
    });
  }

  let total = mergedMinutes(intervals);

  for (const task of [...overdueTasks, ...dueTodayTasks]) {
    if (blockTaskIds.has(task.id)) continue;
    if (task.status === 'done' || task.status === 'dropped') continue;
    const estimate = task.estimate_minutes;
    if (estimate != null && estimate > 0) total += estimate;
  }

  return total;
}
